// Copyright 2026 AllSpice. All rights reserved.

import type {TreeNodeWithPage} from '../../allspice_app/components/diff-list/diff-grouping.ts';
import {ATTRIBUTES_DELIMITER, makeDiffFilterTargetId} from './diff-filter-shared.ts';

export class FilterItem {
  id: string;
  name: string;
  parent: FilterItem | null;
  isVisible: boolean;
  children: FilterItem[];
  isExpanded: boolean;
  isIndeterminate: boolean;

  constructor(id: string, name: string, parent: FilterItem | null = null) {
    this.id = id;
    this.name = name;
    this.parent = parent;
    this.isVisible = true;
    this.children = [];
    this.isExpanded = false;
    this.isIndeterminate = false;
  }

  toggleVisibility(): void {
    this.isVisible = !this.isVisible;
    this.isIndeterminate = false;
    this.updateChildrenVisibility();
  }

  buildAncestorsChain(): string[] {
    const parts = this.id.split(ATTRIBUTES_DELIMITER);
    return parts.map((_, index) => parts.slice(0, index + 1).join(ATTRIBUTES_DELIMITER));
  }

  updateChildrenVisibility(): void {
    for (const child of this.children) {
      child.isVisible = this.isVisible;
      child.isIndeterminate = false;
      child.updateChildrenVisibility();
    }
  }
}

type GroupedChanges = Record<string, TreeNodeWithPage[]>;

// Keys that should not appear as property sub-groupings in the filter tree.
const IGNORED_PROPERTY_KEYS = new Set(['html_id', 'font_id', 'net_id', 'title', 'revision', 'variantname', 'documentname']);

// Parent keys whose children are keyed by opaque hash IDs.
const HASH_KEYED_PARENTS = new Set(['attributes', 'pins']);

// Graphics sub-types that the old toolbar filter grouped under a single "graphics" parent.
// Includes both plural (singlePageSchematic/schematic) and singular (altiumSchematic) forms.
const GRAPHICS_TYPES = new Set([
  'arc', 'arcs',
  'bezier', 'beziers',
  'bitmap', 'bitmaps',
  'ellipse', 'ellipses',
  'polygon', 'polygons',
  'polyline', 'polylines',
  'rectangle', 'rectangles',
  'text', 'texts',
]);

/**
 * Resolve display name for a TreeNode child under a hash-keyed parent.
 */
function resolveChildDisplayName(child: TreeNodeWithPage, parentKey: string): string {
  if (!HASH_KEYED_PARENTS.has(parentKey)) return child.key;

  const nameChild = child.children.find((c) => c.key === 'name');
  if (nameChild) {
    const head = nameChild.head as {val?: string; rawValue?: () => string} | undefined;
    if (head) {
      const name = head.val ?? head.rawValue?.();
      if (name && name !== 'null' && name !== 'undefined') return name;
    }
    const base = nameChild.base as {val?: string; rawValue?: () => string} | undefined;
    if (base) {
      const name = base.val ?? base.rawValue?.();
      if (name && name !== 'null' && name !== 'undefined') return name;
    }
  }

  return child.key;
}

/**
 * Recursively build FilterItem children from a TreeNode's changed sub-tree,
 * producing paths like `components|x42~|attributes|x42~|Designator|x42~|value`.
 *
 * Graphics-type properties (arcs, beziers, etc.) are merged under a single
 * "graphics" parent to match the old toolbar filter structure.
 */
function buildPropertyFilterItems(
  parentItem: FilterItem,
  parentFilterId: string,
  parentKey: string,
  children: TreeNodeWithPage[],
  dataId: string,
  attributesToDataIds: Record<string, string[]>,
  dataIdsToAttributes: Record<string, string[]>,
): void {
  let graphicsItem: FilterItem | null = null;

  for (const child of children) {
    // Only show properties with actual sub-changes (modified=true). Properties
    // that are purely added or removed without modification are part of a
    // wholesale entity add/remove and don't need individual filter items.
    if (!child.modified) continue;
    if (!child.key || IGNORED_PROPERTY_KEYS.has(child.key)) continue;

    const displayName = resolveChildDisplayName(child, parentKey);
    if (!displayName || displayName === 'null' || displayName === 'undefined') continue;

    // Merge graphics-type properties under a single "graphics" parent.
    const isGraphicsProp = GRAPHICS_TYPES.has(child.key);
    let targetParent = parentItem;
    let targetParentId = parentFilterId;

    if (isGraphicsProp) {
      const graphicsId = `${parentFilterId}${ATTRIBUTES_DELIMITER}graphics`;
      if (!graphicsItem) {
        graphicsItem = parentItem.children.find((c) => c.id === graphicsId) as FilterItem | null;
        if (!graphicsItem) {
          graphicsItem = new FilterItem(graphicsId, 'graphics', parentItem);
          parentItem.children.push(graphicsItem);
        }
      }
      targetParent = graphicsItem;
      targetParentId = graphicsId;
    }

    // Use lowercase for filter IDs to merge case variants (e.g., TOLERANCE / Tolerance).
    const normalizedName = displayName.toLowerCase();
    const childFilterId = `${targetParentId}${ATTRIBUTES_DELIMITER}${normalizedName}`;

    let childItem = targetParent.children.find((c) => c.id === childFilterId);
    if (!childItem) {
      childItem = new FilterItem(childFilterId, displayName, targetParent);
      targetParent.children.push(childItem);
    }

    if (attributesToDataIds[childFilterId]) {
      attributesToDataIds[childFilterId].push(dataId);
    } else {
      attributesToDataIds[childFilterId] = [dataId];
    }

    if (dataIdsToAttributes[dataId]) {
      dataIdsToAttributes[dataId].push(childFilterId);
    } else {
      dataIdsToAttributes[dataId] = [childFilterId];
    }

    if (child.children.length > 0) {
      buildPropertyFilterItems(childItem, childFilterId, child.key, child.children, dataId, attributesToDataIds, dataIdsToAttributes);
    }
  }

  // Set up graphics parent data IDs if any graphics children were found.
  if (graphicsItem) {
    const graphicsDataIds = new Set<string>();
    for (const child of graphicsItem.children) {
      for (const id of attributesToDataIds[child.id] ?? []) {
        graphicsDataIds.add(id);
      }
    }
    if (attributesToDataIds[graphicsItem.id]) {
      for (const id of graphicsDataIds) {
        if (!attributesToDataIds[graphicsItem.id].includes(id)) {
          attributesToDataIds[graphicsItem.id].push(id);
        }
      }
    } else {
      attributesToDataIds[graphicsItem.id] = Array.from(graphicsDataIds);
    }
  }
}

type GroupedChangesFilterTreeResult = {
  attributesToDataIds: Record<string, string[]>;
  dataIdsToAttributes: Record<string, string[]>;
  filterList: FilterItem[];
};

export function buildGroupedChangesFilterTree(
  groupedChanges: GroupedChanges,
  formatGroupLabel: (groupName: string, fallbackCount: number) => string,
): GroupedChangesFilterTreeResult {
  const attributesToDataIds: Record<string, string[]> = {};
  const dataIdsToAttributes: Record<string, string[]> = {};
  const filterList: FilterItem[] = [];
  let graphicsItem: FilterItem | null = null;

  for (const [groupName, entities] of Object.entries(groupedChanges)) {
    if (entities.length === 0) continue;

    // Merge schematic graphics sub-types (arcs, beziers, etc.) under a single "graphics" parent.
    // Lowercase comparison handles altiumSchematic types which are capitalized (e.g. "Arc").
    const isGraphics = GRAPHICS_TYPES.has(groupName.toLowerCase());
    let groupItem: FilterItem;

    if (isGraphics) {
      if (!graphicsItem) {
        graphicsItem = new FilterItem('graphics', 'graphics');
        filterList.push(graphicsItem);
      }
      groupItem = new FilterItem(`graphics${ATTRIBUTES_DELIMITER}${groupName}`, groupName, graphicsItem);
      graphicsItem.children.push(groupItem);
    } else {
      groupItem = new FilterItem(groupName, groupName);
      filterList.push(groupItem);
    }

    const groupDataIds = new Set<string>();

    for (const entity of entities) {
      const dataId = makeDiffFilterTargetId(entity.page, entity.key);
      groupDataIds.add(dataId);

      // Register every entity in the group-level mapping so group show/hide
      // works even when the entity has no modified properties.
      if (dataIdsToAttributes[dataId]) {
        if (!dataIdsToAttributes[dataId].includes(groupItem.id)) {
          dataIdsToAttributes[dataId].push(groupItem.id);
        }
      } else {
        dataIdsToAttributes[dataId] = [groupItem.id];
      }

      buildPropertyFilterItems(groupItem, groupItem.id, groupName, entity.children, dataId, attributesToDataIds, dataIdsToAttributes);
    }

    attributesToDataIds[groupItem.id] = Array.from(groupDataIds);
    groupItem.children.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    groupItem.name = formatGroupLabel(groupName, entities.length);
  }

  // Set up the graphics parent with aggregated data IDs.
  if (graphicsItem) {
    const allGraphicsDataIds = new Set<string>();
    for (const child of graphicsItem.children) {
      for (const id of attributesToDataIds[child.id] ?? []) {
        allGraphicsDataIds.add(id);
      }
    }
    attributesToDataIds.graphics = Array.from(allGraphicsDataIds);
    graphicsItem.name = formatGroupLabel('graphics', allGraphicsDataIds.size);
  }

  return {
    attributesToDataIds,
    dataIdsToAttributes,
    filterList,
  };
}
